import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Court } from '@court-sync/shared';
import { courtService } from '../services/court';
import { STALE_TIME } from '../queryClient';
import { queryKeys } from './keys';
import { unwrapApiData, unwrapApiDataOrEmpty } from './unwrap';

export function useCourts(academyId?: string) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.courts.list(academyId ?? '');

  const { data: courts = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await courtService.getCourts(academyId);
      return unwrapApiDataOrEmpty(response);
    },
    enabled: !!academyId,
    staleTime: STALE_TIME.MEDIUM,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.courts.lists() });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: async (data: Omit<Court, 'id' | 'createdAt' | 'updatedAt'>) => {
      const response = await courtService.createCourt(data);
      const id = unwrapApiData(response, 'Failed to create court').id;
      const detail = await courtService.getCourt(id);
      return unwrapApiData(detail, 'Failed to fetch created court');
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Omit<Court, 'id' | 'createdAt' | 'updatedAt'>>;
    }) => {
      await courtService.updateCourt(id, data);
      const detail = await courtService.getCourt(id);
      return unwrapApiData(detail, 'Failed to fetch updated court');
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Court[]>(queryKey);
      queryClient.setQueryData<Court[]>(queryKey, (old) =>
        old?.map((court) => (court.id === id ? { ...court, ...data, updatedAt: new Date() } : court))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await courtService.deleteCourt(id);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Court[]>(queryKey);
      queryClient.setQueryData<Court[]>(queryKey, (old) => old?.filter((c) => c.id !== id));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: invalidate,
  });

  const getCourtById = useCallback((id: string) => courts.find((c) => c.id === id), [courts]);

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    courts,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
    createCourt: createMutation.mutateAsync,
    updateCourt: (id: string, data: Parameters<typeof updateMutation.mutateAsync>[0]['data']) =>
      updateMutation.mutateAsync({ id, data }),
    deleteCourt: deleteMutation.mutateAsync,
    getCourtById,
  };
}
